
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Shield, Search } from 'lucide-react';

interface HeroProps {
  onGetStarted: () => void;
}

const Hero = ({ onGetStarted }: HeroProps) => {
  return (
    <section className="py-8 sm:py-12 md:py-16 relative overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0 bg-gradient-to-br from-protec-gold/5 via-transparent to-protec-gold/10"></div>
      <div className="absolute top-20 left-10 sm:left-20 w-32 sm:w-64 h-32 sm:h-64 bg-protec-gold/10 rounded-full blur-3xl"></div>
      <div className="absolute bottom-20 right-10 sm:right-20 w-48 sm:w-96 h-48 sm:h-96 bg-protec-gold/5 rounded-full blur-3xl"></div>
      
      <div className="container mx-auto px-4 relative z-10">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          {/* Left Side - Content */}
          <div className="text-center lg:text-left">
            <Badge className="mb-4 sm:mb-6 bg-protec-gold/20 text-protec-gold border-protec-gold/30 text-xs sm:text-sm">
              <Shield className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
              <span className="hidden xs:inline">Tecnologia de Ponta em Monitoramento</span>
              <span className="xs:hidden">Tecnologia de Ponta</span>
            </Badge>
            
            <h1 className="text-3xl sm:text-5xl md:text-7xl font-bold mb-4 sm:mb-6 leading-tight">
              <span className="gradient-text">PROTEC BENS</span>
            </h1>
            
            <p className="text-base sm:text-xl md:text-2xl text-gray-300 mb-6 sm:mb-8 max-w-3xl leading-relaxed mx-auto lg:mx-0">
              Plataforma avançada de vigilância com rede nacional de câmeras para 
              <span className="text-protec-gold font-semibold"> rastreamento de veículos</span> e 
              <span className="text-protec-gold font-semibold"> reconhecimento facial</span> em tempo real.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start items-center lg:items-start mb-8 sm:mb-12">
              <Button 
                size="lg" 
                className="bg-gradient-gold text-black font-bold px-6 sm:px-8 py-3 sm:py-4 text-base sm:text-lg hover:shadow-gold transform hover:scale-105 transition-all w-full sm:w-auto"
                onClick={onGetStarted}
              >
                <Search className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                Começar Investigação
              </Button>
            </div>
            
            {/* Key Features Pills */}
            <div className="flex flex-wrap gap-2 sm:gap-3 justify-center lg:justify-start">
              <Badge variant="outline" className="border-protec-gold/30 text-gray-300 px-2 sm:px-4 py-1 sm:py-2 text-xs sm:text-sm">
                🚗 Rastreamento
              </Badge>
              <Badge variant="outline" className="border-protec-gold/30 text-gray-300 px-2 sm:px-4 py-1 sm:py-2 text-xs sm:text-sm">
                👤 Facial
              </Badge>
              <Badge variant="outline" className="border-protec-gold/30 text-gray-300 px-2 sm:px-4 py-1 sm:py-2 text-xs sm:text-sm">
                📹 Ao Vivo
              </Badge>
              <Badge variant="outline" className="border-protec-gold/30 text-gray-300 px-2 sm:px-4 py-1 sm:py-2 text-xs sm:text-sm">
                🗺️ Mapa
              </Badge>
              <Badge variant="outline" className="border-protec-gold/30 text-gray-300 px-2 sm:px-4 py-1 sm:py-2 text-xs sm:text-sm">
                📊 Relatórios
              </Badge>
            </div>
          </div>

          {/* Right Side - Live Camera */}
          <div className="lg:ml-8 mt-6 lg:mt-0">
            <div className="bg-protec-dark-light border border-protec-gold/30 rounded-lg p-4 sm:p-6 shadow-gold max-w-md mx-auto lg:max-w-none">
              <div className="flex items-center justify-between mb-3 sm:mb-4">
                <h3 className="text-base sm:text-xl font-semibold text-white flex items-center gap-2">
                  📹 Câmera ao Vivo
                </h3>
                <div className="flex items-center gap-1 sm:gap-2">
                  <div className="w-2 h-2 sm:w-3 sm:h-3 bg-green-400 rounded-full animate-pulse"></div>
                  <span className="text-xs sm:text-sm text-green-400">Online</span>
                </div>
              </div>
              
              {/* Simulated Camera Feed */}
              <div className="aspect-video bg-protec-dark rounded-lg overflow-hidden relative">
                <div className="absolute inset-0 bg-gradient-to-br from-gray-800 to-gray-900 flex items-center justify-center">
                  <div className="text-center">
                    <div className="w-10 h-10 sm:w-16 sm:h-16 border-4 border-protec-gold border-t-transparent rounded-full animate-spin mx-auto mb-3 sm:mb-4"></div>
                    <p className="text-gray-400 text-sm sm:text-base">Conectando...</p>
                  </div>
                </div>
                
                {/* Camera Info Overlay */}
                <div className="absolute top-1 sm:top-2 left-1 sm:left-2 bg-black/70 text-white text-[10px] sm:text-xs px-1.5 sm:px-2 py-0.5 sm:py-1 rounded">
                  CAM 001 - São Paulo/SP
                </div>
                <div className="absolute top-1 sm:top-2 right-1 sm:right-2 bg-red-600 text-white text-[10px] sm:text-xs px-1.5 sm:px-2 py-0.5 sm:py-1 rounded">
                  ● REC
                </div>
                <div className="absolute bottom-1 sm:bottom-2 right-1 sm:right-2 bg-black/70 text-white text-[10px] sm:text-xs px-1.5 sm:px-2 py-0.5 sm:py-1 rounded">
                  {new Date().toLocaleTimeString('pt-BR')}
                </div>
              </div>
              
              <div className="mt-3 sm:mt-4 text-center">
                <p className="text-xs sm:text-sm text-gray-400">
                  Avenida Paulista, 1000 - Centro
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
